import { ChatMessage } from '../types'
import { TranscriptionSegment } from './transcriptionService'

export type TranscriptEntry = {
  id: string
  timestamp: string
  type: 'chat' | 'transcription'
  participantId: string
  participantName: string
  content: string
  confidence?: number
  isFinal?: boolean
}

export type UnifiedTranscript = {
  sessionId: string
  startTime: string
  endTime: string
  participants: string[]
  entries: TranscriptEntry[]
  summary?: {
    totalDuration: number
    chatMessageCount: number
    transcriptionSegmentCount: number
    participantSpeakingTime: Record<string, number>
  }
}

class TranscriptService {
  private chatMessages: ChatMessage[] = []
  private transcriptionSegments: TranscriptionSegment[] = []
  private participantNames: Map<string, string> = new Map()

  addChatMessage(message: ChatMessage) {
    this.chatMessages.push(message)
    this.participantNames.set(message.userId.toString(), message.userName)
  }

  addTranscriptionSegment(segment: TranscriptionSegment) {
    this.transcriptionSegments.push(segment)
    if (segment.participantId && segment.speaker) {
      this.participantNames.set(segment.participantId, segment.speaker)
    }
  }

  generateTranscript(sessionId: string): UnifiedTranscript {
    const entries: TranscriptEntry[] = []

    // Convert chat messages to transcript entries
    this.chatMessages.forEach(msg => {
      entries.push({
        id: msg.id,
        timestamp: msg.timestamp,
        type: 'chat',
        participantId: msg.userId.toString(),
        participantName: msg.userName,
        content: `[Chat] ${msg.message}`,
        isFinal: true
      })
    })

    // Convert transcription segments to transcript entries
    this.transcriptionSegments.forEach(segment => {
      const participantName = segment.speaker ||
        this.participantNames.get(segment.participantId || '') ||
        `Speaker ${segment.participantId}`

      entries.push({
        id: segment.id,
        timestamp: segment.timestamp,
        type: 'transcription',
        participantId: segment.participantId || 'unknown',
        participantName,
        content: `[Audio] ${segment.text}`,
        confidence: segment.confidence,
        isFinal: segment.isFinal
      })
    })

    // Sort entries by timestamp
    entries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

    // Calculate summary statistics
    const startTime = entries.length > 0 ? entries[0].timestamp : new Date().toISOString()
    const endTime = entries.length > 0 ? entries[entries.length - 1].timestamp : new Date().toISOString()

    const participantSpeakingTime: Record<string, number> = {}
    const participants = Array.from(new Set(entries.map(e => e.participantName)))

    // Calculate speaking time based on transcription segments
    this.transcriptionSegments.forEach(segment => {
      if (segment.speaker && segment.end && segment.start) {
        const duration = segment.end - segment.start
        participantSpeakingTime[segment.speaker] =
          (participantSpeakingTime[segment.speaker] || 0) + duration
      }
    })

    return {
      sessionId,
      startTime,
      endTime,
      participants,
      entries,
      summary: {
        totalDuration: new Date(endTime).getTime() - new Date(startTime).getTime(),
        chatMessageCount: this.chatMessages.length,
        transcriptionSegmentCount: this.transcriptionSegments.filter(s => s.isFinal).length,
        participantSpeakingTime
      }
    }
  }

  exportTranscriptAsText(transcript: UnifiedTranscript): string {
    let output = `Session Transcript: ${transcript.sessionId}\n`
    output += `Duration: ${new Date(transcript.startTime).toLocaleString()} - ${new Date(transcript.endTime).toLocaleString()}\n`
    output += `Participants: ${transcript.participants.join(', ')}\n\n`

    if (transcript.summary) {
      output += `Summary:\n`
      output += `- Total Duration: ${Math.round(transcript.summary.totalDuration / 1000 / 60)} minutes\n`
      output += `- Chat Messages: ${transcript.summary.chatMessageCount}\n`
      output += `- Audio Transcription Segments: ${transcript.summary.transcriptionSegmentCount}\n\n`

      if (Object.keys(transcript.summary.participantSpeakingTime).length > 0) {
        output += `Speaking Time:\n`
        Object.entries(transcript.summary.participantSpeakingTime).forEach(([participant, time]) => {
          output += `- ${participant}: ${Math.round(time)} seconds\n`
        })
        output += '\n'
      }
    }

    output += `Transcript:\n`
    output += `${'='.repeat(50)}\n`

    transcript.entries.forEach(entry => {
      const time = new Date(entry.timestamp).toLocaleTimeString()
      const confidence = entry.confidence ? ` (${Math.round(entry.confidence * 100)}%)` : ''
      output += `[${time}] ${entry.participantName}: ${entry.content}${confidence}\n`
    })

    return output
  }

  exportTranscriptAsJSON(transcript: UnifiedTranscript): string {
    return JSON.stringify(transcript, null, 2)
  }

  exportTranscriptAsCSV(transcript: UnifiedTranscript): string {
    let csv = 'Timestamp,Type,Participant,Content,Confidence\n'

    transcript.entries.forEach(entry => {
      const confidence = entry.confidence ? entry.confidence.toString() : ''
      const content = entry.content.replace(/"/g, '""') // Escape quotes
      csv += `"${entry.timestamp}","${entry.type}","${entry.participantName}","${content}","${confidence}"\n`
    })

    return csv
  }

  clearTranscript() {
    this.chatMessages = []
    this.transcriptionSegments = []
    this.participantNames.clear()
  }

  // Method to be called when session ends to generate final transcript
  finalizeSession(sessionId: string): UnifiedTranscript {
    const transcript = this.generateTranscript(sessionId)

    // Log transcript generation
    console.log('Generated unified transcript:', {
      sessionId,
      entryCount: transcript.entries.length,
      participants: transcript.participants.length,
      duration: transcript.summary?.totalDuration
    })

    return transcript
  }
}

export const transcriptService = new TranscriptService()
export default transcriptService